import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { changeOpenedModal } from '../../redux/actions/headerModal';
import LoadingAvatar from '../../components/LoadingAvatar';
import { useTranslation } from 'react-i18next';
import { clearBuilding } from '../../redux/actions/building';
import {
  clearService,
  closeChatModal,
  getServiceRequestCategories,
  openChatModal,
  updateService
} from '../../redux/actions/services';
import { LOADING_BLUE } from '../../utils/colorCode';
import { STATUS_LABEL_ASSIGNED, RequestStatus, isOrgWithOrgWideYardiLookup } from './constant';
import { getContactWithFromId, getPriorityFromId } from '../../helper';
import * as Yup from 'yup';
import {
  HeaderText,
  ModalBodyContent,
  ModalButtonsContainer,
  StyledModalBody,
  StyledModalTitle,
  StyledModalV2
} from '../../components/Modals';
import { ButtonBodyVariant } from '../../components/ui/ButtonVariant';
import { Fonts, Palette } from '../../components/utils';
import styled, { css } from 'styled-components';
import { Flex } from '../../components/ui/Flex';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '../../components/ui/Form';
import { useForm } from 'react-hook-form';
import { Separator } from '../../components/ui/Separator';
import {
  SettingsItem,
  SettingsItemContent,
  SettingsItemSubTitle,
  SettingsItemTitle
} from '../settings/SettingsItem';
import { FaBuilding, FaUser } from 'react-icons/fa';
import { FaDog } from 'react-icons/fa6';
import { HiChatBubbleOvalLeft } from 'react-icons/hi2';
import { MdContactPage, MdDescription, MdError } from 'react-icons/md';
import { PiWrenchFill } from 'react-icons/pi';
import { BiSolidBellRing } from 'react-icons/bi';
import { Select, SelectItem } from '../../components/ui/Select';
import UserAsyncDropDown from '../../components/UserAsyncDropDown';
import TechnicianNotesSection from './TechnicianNotesSection';
import {
  buildTechnicianNoteItemsPayload,
  cloneTechnicianNoteItems,
  hasEmptyTechnicianNote,
  hasValidTechnicianNotes
} from './technicianNotesUtils';
import { closeActiveModal, showToast } from '../../helper/modals';
import { SEARCH_TABLES } from '../../helper/constants';
import { searchTable, setSearchPagination, setSearchState } from '../../redux/actions/search';
import { setUserSearch } from '../../redux/actions/user';
import {
  CALL,
  COMPLETE,
  IN_PROGRESS,
  ON_HOLD,
  OPEN,
  REASSIGNMENT,
  SCHEDULED,
  WEB
} from '../../utils/mappings';
import { yupResolver } from '@hookform/resolvers/yup';
import ServiceDetailChatModal from './serviceDetailSubComponents/ServiceDetailChatModal';
import { CModalHeader } from '@coreui/react';
import { useScrollToBottom } from '../../hooks/useScrollToBottom';
import { isYardiOrganization } from '../users/constant';
import { filterCategoriesForServiceRequestDropdown } from './categories';

const STATUS_MAP = [OPEN, IN_PROGRESS, COMPLETE];
const YARDI_STATUS_MAP = [CALL, SCHEDULED, ON_HOLD, REASSIGNMENT, WEB];
const settingsItemTitleAltStyles = {
  color: Palette.BLACK_50,
  fontSize: '10px',
  lineHeight: '16px',
  textTransform: 'uppercase'
};

const ServiceDetail = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const modal = useSelector((state) => state.headerModal.modal);
  const status = useSelector((state) => state.services.status);
  const error = useSelector((state) => state.services.error);
  const categories = useSelector((state) => state.services.categories);
  const searchState = useSelector((state) => state.search);

  const service = useSelector((state) => state.services.service);
  const shouldShowChatModal = useSelector((state) => state.services.shouldShowChatModal);
  const globalOrgId = useSelector((state) => state.Organization.globalOrgId);

  const organization = useSelector((state) => state.Organization.organization);
  const allBuildings = useSelector((state) => state.building.allBuildings);
  const workRequestBuilding = allBuildings.find((building) => building.id === service.building_id);
  
  let buildingForYardiCheck = workRequestBuilding;
  const isYardiBuilding = Boolean(buildingForYardiCheck?.yardi_id);

  // Find fallback Yardi building if workRequestBuilding is not a Yardi building and org-wide lookup is enabled
  if (isYardiOrganization(organization) && buildingForYardiCheck && !isYardiBuilding) {
    if (isOrgWithOrgWideYardiLookup(globalOrgId)) {
      const fallbackYardiBuilding = allBuildings.find(
        (building) =>
          building.org_id === buildingForYardiCheck.org_id &&
          Boolean(building?.yardi_id) &&
          building.id !== buildingForYardiCheck.id
      );
      
      if (fallbackYardiBuilding) {
        buildingForYardiCheck = fallbackYardiBuilding;
      }
    }
  }
  
  const isYardiWorkRequestsEnabled = Boolean(
    buildingForYardiCheck?.sync_config?.enable_yardi_work_requests
  );

  const availableStatuses = isYardiWorkRequestsEnabled
    ? [...STATUS_MAP, ...YARDI_STATUS_MAP]
    : STATUS_MAP;

  const categoryDropdownOptions = useMemo(
    () => filterCategoriesForServiceRequestDropdown(categories, service?.category_id),
    [categories, service?.category_id]
  );

  const modalBodyRef = useRef(null);
  const { isAtBottom } = useScrollToBottom(modalBodyRef);

  const isDisabled =
    service.status === RequestStatus.ARCHIVED || service.status === RequestStatus.COMPLETE;

  const shouldShowTechnicianNotes = !(
    service.status === RequestStatus.COMPLETE &&
    !hasValidTechnicianNotes(service.technician_note_items)
  );

  const schema = Yup.object().shape({
    category: Yup.number().required(t('Category is required')),
    status: Yup.number().required(t('Status is required')),
    technician_note_items: Yup.array().of(
      Yup.object().shape({
        note: Yup.string().trim().required(t('Note cannot be empty'))
      })
    ),
    technician: Yup.mixed().when('status', {
      is: IN_PROGRESS.id,
      then: Yup.object()
        .required(t('Technician is required'))
        .shape({
          value: Yup.number().required(t('Technician ID is required')),
          label: Yup.string().required(t('Technician name is required'))
        })
        .default(undefined)
        .typeError(t('Technician must be selected when status is In Progress')),
      otherwise: Yup.mixed()
        .notRequired()
        .nullable()
        .transform((val) => {
          return val?.value ? val : undefined;
        })
    })
  });
  const form = useForm({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: {
      category: undefined,
      technician: undefined,
      status: undefined,
      technician_note_items: []
    }
  });

  const watchStatus = form.watch('status');
  const watchNotes = form.watch('technician_note_items') || [];

  const isCompleteBlocked =
    watchStatus === COMPLETE.id &&
    service.technician_notes_required &&
    !hasValidTechnicianNotes(watchNotes);

  const hasEmptyNote = hasEmptyTechnicianNote(watchNotes);

  const onSubmit = (values) => {
    const body = {
      user_id: parseInt(service.user_id),
      building_id: parseInt(service.building_id),
      subject: service.subject,
      description: service.description,
      technician_note_items: buildTechnicianNoteItemsPayload(values.technician_note_items),
      media: service.media,
      time_slot: service.time_slot ? service.time_slot : null,
      contact_type: service.contact_type,
      priority: service.priority,
      allow_access: service.allow_access,
      has_pets: Boolean(service.has_pets),
      assigned_to: values.technician?.value || 0,
      category_id: values.category,
      schedule_date: service.schedule_date,
      status_id: values.status,
      data: service.data,
      contact_info: service.contact_info
    };

    dispatch(
      updateService({
        id: service.id,
        body: body,
        success: () => {
          dispatch(
            searchTable({
              entity: SEARCH_TABLES.WORK_REQUESTS,
              orgID: globalOrgId,
              body: {
                search: searchState.search,
                minCreationDate: searchState.minCreationDate,
                maxCreationDate: searchState.maxCreationDate,
                filters: searchState.filters,
                ordering: searchState.ordering,
                pagination: searchState.pagination
              },
              success: (res) => {
                dispatch(
                  setSearchState({
                    currentData: res.data.data ?? [],
                    currentPaginationData: res.data.pagination
                  })
                );

                if (!res.data.data) {
                  dispatch(setUserSearch({ page: 1 }));
                  dispatch(
                    setSearchPagination({
                      page: 1,
                      limit: 20
                    })
                  );
                }
              }
            })
          );
          showToast(t('Service request details have been successfully updated!'));
          closeActiveModal();
        },
        fail: (res) => {
          console.log(res);
          showToast(t('Service request update failed!'), true);
        }
      })
    );
  };

  const onClose = () => {
    dispatch(clearService());
    dispatch(clearBuilding());
    dispatch(
      changeOpenedModal({
        modal: ''
      })
    );
  };

  useEffect(() => {
    if (service && !form.formState.isDirty) {
      form.reset({
        category: service.category_id,
        technician:
          service.assigned_to && service.assigned_to_name
            ? { value: service.assigned_to, label: service.assigned_to_name }
            : null,
        status: service.status_id,
        technician_note_items: cloneTechnicianNoteItems(service.technician_note_items)
      });
    }

    dispatch(getServiceRequestCategories({ orgId: globalOrgId }));
  }, [service, form.reset, form.formState.isDirty]);

  return !shouldShowChatModal ? (
    <Form {...form}>
      <form name="createServiceReqForm" onSubmit={form.handleSubmit(onSubmit)}>
        <StyledModalV2
          show={modal === 'serviceDetail'}
          padding
          side
          width="420px"
          onClose={onClose}>
          {status === 'GET_SERVICE_PENDING' ? (
            <LoadingAvatar color={LOADING_BLUE} />
          ) : status === 'GET_SERVICE_FAIL' ? (
            <ErrorContainer direction="column" justify="center" alignItems="center" gap="16px">
              <ErrorText>{error}</ErrorText>
              <ButtonBodyVariant type="button" variant="secondary" onClick={onClose}>
                {t('Close')}
              </ButtonBodyVariant>
            </ErrorContainer>
          ) : (
            <>
              <StyledModalBody innerRef={(el) => (modalBodyRef.current = el)}>
                <CModalHeader>
                  <Flex direction="column">
                    <RefText>{service?.reference}</RefText>
                    <StyledModalTitle>{service?.subject}</StyledModalTitle>
                  </Flex>

                  {!service.is_admin_request && (
                    <ChatBoxButton onClick={() => dispatch(openChatModal())} />
                  )}
                </CModalHeader>
                <ModalBodyContent>
                  <Flex direction="column" gap="16px">
                    <FormField
                      control={form.control}
                      name="status"
                      render={({ field }) => (
                        <FormItem alt>
                          <FormLabel v3>{t('Ticket Status')}</FormLabel>
                          <FormControl>
                            <Select
                              {...field}
                              disabled={isDisabled}
                              placeholder={t('Please Select')}
                              onChange={(e) => {
                                if (e !== IN_PROGRESS.id && e !== COMPLETE.id) {
                                  form.setValue('technician', null);
                                }
                                field.onChange(e);
                              }}>
                              {availableStatuses.map((status) => (
                                <SelectItem key={status.id} value={status.id}>
                                  {status.id === IN_PROGRESS.id
                                    ? STATUS_LABEL_ASSIGNED
                                    : status.value}
                                </SelectItem>
                              ))}
                            </Select>
                          </FormControl>

                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Separator orientation="horizontal" />

                    <Flex direction="column" gap="20px">
                      <HeaderText alt>{t('Resident details')}</HeaderText>

                      <Flex justify="space-between" gap="19px">
                        <SettingsItem>
                          <FaUser size={18} color={Palette.HAZY_BLUE} className="flex-shrink-0" />
                          <SettingsItemContent>
                            <SettingsItemTitle cssOverride={settingsItemTitleAltStyles}>
                              {t('Resident')}
                            </SettingsItemTitle>
                            <SettingsItemSubTitle>
                              {service.is_admin_request
                                ? t('None')
                                : service.tenant
                                  ? service.tenant.name
                                  : t('User not found')}
                            </SettingsItemSubTitle>
                          </SettingsItemContent>
                        </SettingsItem>

                        <SettingsItem>
                          <FaBuilding
                            size={18}
                            color={Palette.HAZY_BLUE}
                            className="flex-shrink-0"
                          />
                          <SettingsItemContent>
                            <SettingsItemTitle cssOverride={settingsItemTitleAltStyles}>
                              {t('Building')}
                            </SettingsItemTitle>
                            <SettingsItemSubTitle>
                              {service.tenant ? service.tenant.building : t('Building not found')}
                            </SettingsItemSubTitle>
                            {!service.is_admin_request && (
                              <SettingsItemSubTitle>
                                {service.tenant
                                  ? `Floor ${service.tenant.floor} Unit ${service.tenant.unit_num}`
                                  : t('Unit not found')}
                              </SettingsItemSubTitle>
                            )}
                          </SettingsItemContent>
                        </SettingsItem>
                      </Flex>
                    </Flex>

                    <Separator orientation="horizontal" />

                    <Flex direction="column" gap="20px">
                      <HeaderText alt>{t('Service details')}</HeaderText>

                      <Flex direction="column" gap="16px">
                        <GridTest>
                          <SettingsItem>
                            <MdDescription
                              size={18}
                              color={Palette.HAZY_BLUE}
                              className="flex-shrink-0"
                            />
                            <SettingsItemContent>
                              <SettingsItemTitle cssOverride={settingsItemTitleAltStyles}>
                                {t('Contact method')}
                              </SettingsItemTitle>
                              <SettingsItemSubTitle>
                                {service.is_admin_request && !(service.contact_type > 0)
                                  ? t('None')
                                  : getContactWithFromId(service.contact_type)}
                              </SettingsItemSubTitle>
                            </SettingsItemContent>
                          </SettingsItem>

                          <SettingsItem cssOverride={{ justifySelf: 'end' }}>
                            <PiWrenchFill
                              size={18}
                              color={Palette.HAZY_BLUE}
                              className="flex-shrink-0"
                            />
                            <SettingsItemContent>
                              <SettingsItemTitle cssOverride={settingsItemTitleAltStyles}>
                                {t('Unit Access')}
                              </SettingsItemTitle>
                              <SettingsItemSubTitle>
                                {service.allow_access ? t('Allowed') : t('Not allowed')}
                              </SettingsItemSubTitle>
                            </SettingsItemContent>
                          </SettingsItem>

                          <SettingsItem>
                            <BiSolidBellRing
                              size={18}
                              color={Palette.HAZY_BLUE}
                              className="flex-shrink-0"
                            />
                            <SettingsItemContent>
                              <SettingsItemTitle cssOverride={settingsItemTitleAltStyles}>
                                {t('Urgency')}
                              </SettingsItemTitle>
                              <SettingsItemSubTitle>
                                {getPriorityFromId(service.priority)}
                              </SettingsItemSubTitle>
                            </SettingsItemContent>
                          </SettingsItem>
                        </GridTest>
                        <SettingsItem>
                          <MdContactPage
                            size={18}
                            color={Palette.HAZY_BLUE}
                            className="flex-shrink-0"
                          />
                          <SettingsItemContent>
                            <SettingsItemTitle cssOverride={settingsItemTitleAltStyles}>
                              {t('Description')}
                            </SettingsItemTitle>
                            <SettingsItemSubTitle>{service.description}</SettingsItemSubTitle>
                          </SettingsItemContent>
                        </SettingsItem>

                        <SettingsItem>
                          <FaDog
                            size={18}
                            color={Palette.HAZY_BLUE}
                            className="flex-shrink-0"
                          />
                          <SettingsItemContent>
                            <SettingsItemTitle cssOverride={settingsItemTitleAltStyles}>
                              {t('Pets')}
                            </SettingsItemTitle>
                            <SettingsItemSubTitle>
                              {service.has_pets ? t('Yes') : t('No')}
                            </SettingsItemSubTitle>
                          </SettingsItemContent>
                        </SettingsItem>
                      </Flex>

                      {service?.media !== '' && (
                        <div>
                          <PhotosTitle>Photos</PhotosTitle>
                          <PhotosContainer>
                            <a href={service.media} target="_blank">
                              <img src={service.media} alt={service.subject} />
                            </a>
                          </PhotosContainer>
                        </div>
                      )}

                      <FormField
                        control={form.control}
                        name="technician"
                        render={({ field }) => {
                          const statusValue = form.getValues('status');
                          return (
                            <FormItem alt>
                              <FormLabel v3>{t('Assigned technician')}</FormLabel>
                              <FormControl>
                                <UserAsyncDropDown
                                  skip_deleted={true}
                                  type={2}
                                  placeholder={t('Assign to')}
                                  disabled={statusValue !== IN_PROGRESS.id || isDisabled}
                                  buildingId={service.building_id}
                                  allAccessible
                                  {...field}
                                />
                              </FormControl>

                              <FormMessage />
                            </FormItem>
                          );
                        }}
                      />

                      <FormField
                        control={form.control}
                        name="category"
                        render={({ field }) => (
                          <FormItem alt>
                            <FormLabel v3>{t('Category')}</FormLabel>
                            <FormControl>
                              <Select
                                custom
                                disabled={isDisabled}
                                placeholder={'Please select'}
                                {...field}>
                                {categoryDropdownOptions.map((item) => (
                                  <SelectItem key={item.ID} value={item.ID}>
                                    {item.Name}
                                    {item?.SubCategory !== '' ? `/${item.SubCategory}` : ''}
                                  </SelectItem>
                                ))}
                              </Select>
                            </FormControl>

                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {shouldShowTechnicianNotes && (
                        <>
                          <TechnicianNotesSection form={form} disabled={isDisabled} />
                          {isCompleteBlocked && (
                            <StyledErrorText>
                              <MdError size={24} />
                              {t('Technician notes are required.')}
                            </StyledErrorText>
                          )}
                        </>
                      )}
                    </Flex>
                  </Flex>
                </ModalBodyContent>
              </StyledModalBody>
              <ModalButtonsContainer gapY="8px" justify="flex-end" isAtBottom={isAtBottom}>
                <ButtonBodyVariant type="button" variant="secondary" onClick={onClose}>
                  {t('Cancel')}
                </ButtonBodyVariant>
                <ButtonBodyVariant
                  type="submit"
                  disabled={
                    !form.formState.isDirty ||
                    form.formState.isSubmitting ||
                    isCompleteBlocked ||
                    hasEmptyNote
                  }>
                  {t('Update')}
                </ButtonBodyVariant>
              </ModalButtonsContainer>
            </>
          )}
        </StyledModalV2>
      </form>
    </Form>
  ) : (
    <ServiceDetailChatModal
      onClose={onClose}
      onClickBack={() => dispatch(closeChatModal())}
      isDisabled={isDisabled}
    />
  );
};

const RefText = styled.p`
  color: ${Palette.BLACK};
  font-family: ${Fonts.OPEN_SANS_FONT};
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 16px;
  margin: 0;
`;

const GridTest = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  gap: 24px 0;
  justify-content: space-around;
  align-content: space-around;
`;

const iconStyles = css`
  color: ${Palette.HAZY_BLUE};

  :hover {
    color: ${Palette.BLACK};
    cursor: pointer;
  }
`;

const ChatBoxButton = styled(HiChatBubbleOvalLeft)`
  width: 23px;
  height: 21.3px;
  flex-shrink: 0;
  ${iconStyles}
`;

const ErrorText = styled.p`
  color: ${Palette.ERROR};
  font-family: ${Fonts.OPEN_SANS_FONT};
  font-size: 16px;
  text-align: left;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  text-align: center;
  margin: 0;
`;

const PhotosTitle = styled.p`
  margin-bottom: 16px;
  color: ${Palette.BLACK};
  font-size: 16px;
  font-weight: 600;
`;

const PhotosContainer = styled.div`
  --photo-width: 100px;
  display: grid;
  grid-template-columns: repeat(auto-fit, var(--photo-width));
  gap: 24px;

  & img {
    background-color: ${Palette.STROKE_GREY};
    width: 100%;
    height: var(--photo-width);
    object-fit: cover;
    border-radius: 5px;
  }
`;

const ErrorContainer = styled(Flex)`
  height: 100vh;
`;

const StyledErrorText = styled.p`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${Palette.ERROR};
  font-family: ${Fonts.OPEN_SANS_FONT};
  font-size: 12px;
  font-weight: 600;
`;

export default ServiceDetail;
